import type { OfferView } from "../api/client.js";

export const LOCKED_BOOKMAKER_ORDER = [
  "sportsbet",
  "bet365",
  "tab",
  "pointsbet",
  "ladbrokes",
  "dabble",
  "picklebet",
  "betr",
  "nextbet",
  "unibet",
  "betright",
] as const;

const bookmakerOrder = new Map<string, number>(
  LOCKED_BOOKMAKER_ORDER.map((bookmaker, index) => [bookmaker, index]),
);

const BOOKMAKERS: Record<string, { name: string; asset: string }> = {
  sportsbet: { name: "Sportsbet", asset: "/bookmakers/sportsbet.svg" },
  bet365: { name: "Bet365", asset: "/bookmakers/bet365.svg" },
  tab: { name: "TAB", asset: "/bookmakers/tab.svg" },
  pointsbet: { name: "PointsBet", asset: "/bookmakers/pointsbet.svg" },
  ladbrokes: { name: "Ladbrokes", asset: "/bookmakers/ladbrokes.svg" },
  dabble: { name: "Dabble", asset: "/bookmakers/dabble.svg" },
  picklebet: { name: "Picklebet", asset: "/bookmakers/picklebet.svg" },
  betr: { name: "Betr", asset: "/bookmakers/betr.svg" },
  nextbet: { name: "NextBet", asset: "/bookmakers/nextbet.svg" },
  unibet: { name: "Unibet", asset: "/bookmakers/fallback.svg" },
  betright: { name: "BetRight", asset: "/bookmakers/fallback.svg" },
  draftkings: { name: "DraftKings", asset: "/bookmakers/draftkings.svg" },
  pinnacle: { name: "Pinnacle", asset: "/bookmakers/fallback.svg" },
};

export const bookmakerName = (bookmaker: string): string =>
  BOOKMAKERS[bookmaker]?.name ?? bookmaker;

/**
 * Best price first, one tile per bookmaker and line.
 *
 * A book can price the same bet twice - an NHL anytime goalscorer as a yes/no
 * market and again as a 1+ milestone - and showing both put two Sportsbet
 * tiles side by side, one "Below min" and one "Bet". Only the better price is
 * the one to take, so the other is dropped here, once, for every sport.
 */
export function sortAuOffers(
  offers: readonly OfferView[],
): readonly OfferView[] {
  const seen = new Set<string>();
  return [...offers]
    .sort((left, right) => {
      const priceDifference = right.price - left.price;
      if (priceDifference !== 0) return priceDifference;
      return (
        (bookmakerOrder.get(left.bookmaker) ?? Number.MAX_SAFE_INTEGER) -
        (bookmakerOrder.get(right.bookmaker) ?? Number.MAX_SAFE_INTEGER)
      );
    })
    .filter((offer) => {
      const key = `${offer.bookmaker}|${String(offer.line)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function BookmakerBrand({
  bookmaker,
  reference = false,
}: {
  bookmaker: string;
  reference?: boolean;
}) {
  const brand = BOOKMAKERS[bookmaker] ?? {
    name: bookmaker,
    asset: "/bookmakers/fallback.svg",
  };
  // The wordmark already carries the name; a book with no wordmark gets its
  // name as text rather than the generic placeholder image.
  const wordmark = !brand.asset.endsWith("fallback.svg");
  return (
    <span className={`bookmaker-brand${reference ? " reference" : ""}`}>
      {wordmark ? (
        <img
          className="bookmaker-logo"
          src={brand.asset}
          alt={`${brand.name} logo`}
        />
      ) : null}
      <span className={`bookmaker-name${wordmark ? " sr-only" : ""}`}>
        {brand.name}
      </span>
    </span>
  );
}
